import { TILE } from './constants';
import { isqrt } from '../lib/intmath';
import { unitDef } from '../content/units';
import { isWalkable } from './path';
import { UnitMode } from './types';
import type { Grid } from './path';
import type { CombatUnit } from './combat';

/*
 * הפרדה מקומית בין יחידות.
 *
 * **בלי זה יחידות עוברות אחת דרך השנייה ונערמות על אותה נקודה.** פיזור התצורה
 * פותר את היעד, לא את הדרך: כוח שנע יחד, יחידות שרודפות אותה מטרה, וכל מה
 * שיוצא מאותו מפעל — כולם מתלכדים לגוש אחד שנראה כמו יחידה אחת.
 *
 * הפתרון הוא דחיפה, לא חסימה. יחידה חסומה צריכה לחשב מסלול מחדש ולעצור, ובקבוצה
 * זה מייצר פקקים; דחיפה הדדית עדינה שומרת על התנועה ועדיין מפרידה.
 *
 * שלושה כללים ששומרים על הדטרמיניזם:
 *   1. **מתמטיקה שלמה בלבד**, כולל `isqrt` במקום שורש צף.
 *   2. **הדחיפות נצברות ומוחלות בסוף**, לא תוך כדי הסריקה. אחרת מי שנסרק ראשון
 *      נדחף לפי מצב שונה ממי שנסרק אחריו — אותה מלכודת בדיוק שהייתה בירי.
 *   3. **סדר קבוע**: הזוגות נסרקים לפי אינדקס, וזוג נבדק פעם אחת בלבד.
 */

/** איזה חלק מהחפיפה נפתר בטיק אחד. חלקי בכוונה — דחיפה מלאה נראית כמו קפיצה. */
const RELIEF_PCT = 45;

/**
 * תקרת דחיפה לטיק, כאחוז ממהירות היחידה.
 *
 * **זה לא כוונון אלא תנאי נכונות.** בגרסה הראשונה התקרה הייתה מוחלטת (חמישית
 * אריח), וזה גדול ממהירות הקוצר — שתי יחידות איטיות שנפגשו נדחפו אחורה יותר
 * ממה שהתקדמו, ונתקעו זו מול זו לנצח. `simBatch` ראה את זה כמחזור כרייה שקפא:
 * שני קוצרים באותו אריח, מסלול באורך 8 שלא התקצר, ואשראי שנעצר.
 *
 * כל עוד הדחיפה קטנה מהמהירות, **התנועה תמיד מנצחת** וההפרדה רק מסיטה הצידה.
 */
const PUSH_PCT_OF_SPEED = 50;

/**
 * כיוונים לפיזור יחידות שנמצאות **בדיוק** באותה נקודה, שם אין וקטור להיפרד לפיו.
 * שמונה כיוונים בסולם 128, כדי שהחלוקה תישאר שלמה.
 */
const FALLBACK_DIRS: readonly (readonly [number, number])[] = [
    [128, 0],
    [90, 90],
    [0, 128],
    [-90, 90],
    [-128, 0],
    [-90, -90],
    [0, -128],
    [90, -90],
];

export function separateUnits(units: readonly CombatUnit[], grid: Grid): void {
    if (units.length < 2) return;

    const pushX = new Int32Array(units.length);
    const pushY = new Int32Array(units.length);

    /*
     * דלי לכל אריח. בלי זה ההשוואה היא כל-אחד-מול-כולם, וזה מה שיהפוך קרב של
     * 300 יחידות ל-45,000 בדיקות בכל טיק.
     */
    const buckets = new Map<number, number[]>();
    for (let i = 0; i < units.length; i++) {
        const unit = units[i];
        if (!unit || unit.hp <= 0) continue;
        const key = Math.floor(unit.y / TILE) * grid.w + Math.floor(unit.x / TILE);
        const bucket = buckets.get(key);
        if (bucket) bucket.push(i);
        else buckets.set(key, [i]);
    }

    for (let i = 0; i < units.length; i++) {
        const a = units[i];
        if (!a || a.hp <= 0) continue;
        const tileX = Math.floor(a.x / TILE);
        const tileY = Math.floor(a.y / TILE);
        const radiusA = unitDef(a.type).radius;

        // האריח עצמו ושמונת שכניו: יחידה גדולה יכולה לחפוף מעבר לגבול האריח.
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const bucket = buckets.get((tileY + dy) * grid.w + (tileX + dx));
                if (!bucket) continue;

                for (const j of bucket) {
                    // כל זוג פעם אחת בלבד, ולא יחידה מול עצמה.
                    if (j <= i) continue;
                    const b = units[j];
                    if (!b || b.hp <= 0) continue;
                    /*
                     * **שכבות נפרדות אינן נדחפות זו מזו.** מסוק שמרחף מעל טנק
                     * אינו מתנגש בו, ודחיפה בין השתיים הייתה מזיזה את שניהם
                     * ממקום שהוא חוקי לחלוטין לשניהם.
                     */
                    if (unitDef(a.type).layer !== unitDef(b.type).layer) continue;

                    /*
                     * **קוצר שכורה אינו נדחף.**
                     *
                     * זה מקור הרעידה שבר דיווח עליה. קוצר שעומד על אריח עפרות
                     * נדחף ממנו, `stepHarvester` רואה שהוא כבר לא על עפרות
                     * ומחזיר אותו — ואז הוא נדחף שוב. תנועה מנצחת דחיפה (זה מה
                     * שמונע את הנעילה של מלכודת 36), ולכן הזוג מתנודד לנצח
                     * במקום להתייצב, ונראה כמו שתי יחידות שרועדות.
                     *
                     * **הדחיפה עצמה לא בוטלה** — יחידות שנערמות עדיין מתפזרות.
                     * רק מי שעומד ועושה עבודה מוחרג ממנה.
                     */
                    if (a.mode === UnitMode.Harvesting && b.mode === UnitMode.Harvesting) {
                        continue;
                    }

                    const minDistance = radiusA + unitDef(b.type).radius;
                    const gapX = b.x - a.x;
                    const gapY = b.y - a.y;
                    const squared = gapX * gapX + gapY * gapY;
                    if (squared >= minDistance * minDistance) continue;

                    const distance = isqrt(squared);
                    const overlap = minDistance - distance;
                    // חצי לכל צד: שתי היחידות אשמות בחפיפה במידה שווה.
                    const shove = Math.floor((overlap * RELIEF_PCT) / 200);
                    if (shove <= 0) continue;

                    let stepX: number;
                    let stepY: number;
                    if (distance > 0) {
                        stepX = Math.trunc((gapX * shove) / distance);
                        stepY = Math.trunc((gapY * shove) / distance);
                    } else {
                        /*
                         * שתי יחידות בדיוק באותה נקודה — אין וקטור. בוחרים כיוון
                         * מהטבלה לפי המזהים, כך שהבחירה קבועה בין הרצות ושני זוגות
                         * שונים לא נדחפים כולם לאותו צד.
                         */
                        const dir = FALLBACK_DIRS[(a.id + b.id) & 7] ?? FALLBACK_DIRS[0];
                        stepX = Math.trunc(((dir?.[0] ?? 128) * shove) / 128);
                        stepY = Math.trunc(((dir?.[1] ?? 0) * shove) / 128);
                    }
                    if (stepX === 0 && stepY === 0) stepX = shove;

                    pushX[i] = (pushX[i] ?? 0) - stepX;
                    pushY[i] = (pushY[i] ?? 0) - stepY;
                    pushX[j] = (pushX[j] ?? 0) + stepX;
                    pushY[j] = (pushY[j] ?? 0) + stepY;
                }
            }
        }
    }

    // ---- החלה: אחרי שכל הזוגות נמדדו על אותו מצב בדיוק.
    for (let i = 0; i < units.length; i++) {
        const unit = units[i];
        if (!unit || unit.hp <= 0) continue;
        let dx = pushX[i] ?? 0;
        let dy = pushY[i] ?? 0;
        if (dx === 0 && dy === 0) continue;

        /*
         * חיתוך לפי מהירות היחידה — כאן, אחרי שכל הדחיפות נצברו. חיתוך לכל זוג
         * בנפרד לא היה עוזר: יחידה שמוקפת בשש אחרות מקבלת שש דחיפות שמסתכמות.
         */
        const limit = Math.max(1, Math.floor((unitDef(unit.type).speed * PUSH_PCT_OF_SPEED) / 100));
        const magnitude = isqrt(dx * dx + dy * dy);
        if (magnitude > limit) {
            dx = Math.trunc((dx * limit) / magnitude);
            dy = Math.trunc((dy * limit) / magnitude);
            if (dx === 0 && dy === 0) continue;
        }

        /*
         * דחיפה לא מוציאה יחידה מהמפה ולא דוחפת אותה לתוך סלע. הצירים נבדקים
         * בנפרד, כך שיחידה שנדחפת לפינת קיר עדיין מחליקה לאורכו במקום להיתקע.
         */
        // מטוס אינו מוגבל לטרשת — הוא מוגבל רק לגבולות המפה.
        const air = unitDef(unit.type).layer === 'air';
        const passable = (x: number, y: number) =>
            air
                ? x >= 0 && y >= 0 && x < grid.w * TILE && y < grid.h * TILE
                : isWalkable(grid, Math.floor(x / TILE), Math.floor(y / TILE));

        const nextX = unit.x + dx;
        if (passable(nextX, unit.y)) unit.x = nextX;

        const nextY = unit.y + dy;
        if (passable(unit.x, nextY)) unit.y = nextY;
    }
}
